import { useState } from "react";
import { ManaText } from "@/components/common/ManaSymbol";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Scale } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TONE = { official: "border-amber-400/60 text-amber-200", foundry: "border-emerald-500/60 text-emerald-300", community: "border-sky-500/60 text-sky-300", creator: "border-slate-600 text-slate-400" };
const LABEL = { official: "Official", foundry: "Planar Foundry", community: "Community house ruling", creator: "Creator explanation · non-authoritative" };

export function AuthorityBadge({ authority }) {
  return <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${TONE[authority]}`} data-testid={`ruling-authority-${authority}`}>{LABEL[authority]}</span>;
}

function RulingForm({ targetType, targetId, ownerId, onDone }) {
  const { user } = useAuth();
  const { data: coms = [] } = useQuery({ queryKey: ["me", "communities"], queryFn: () => api.get("/me/communities").then((r) => r.data.items) });
  const staffComs = coms.filter((c) => ["owner", "admin"].includes(c.my_role));
  const options = [user.user_id === ownerId && targetType !== "official_card" && "creator", targetType !== "official_card" && "foundry", staffComs.length && "community"].filter(Boolean);
  const [f, setF] = useState({ authority: "", text: "", community_id: "", source_reference: "" });
  if (!options.length) return null;
  const authority = options.includes(f.authority) ? f.authority : options[0];
  const communityId = f.community_id || staffComs[0]?.community_id || "";
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { authority, target_type: targetType, target_id: targetId, text: f.text, source_reference: f.source_reference, community_id: authority === "community" ? communityId : null };
      const r = await api.post("/rulings", body);
      toast.success(r.data.status === "pending_review" ? "Sent for independent review by two Rules Reviewers" : "Ruling published");
      setF({ ...f, text: "" });
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form className="mt-4 grid gap-2 border-t border-[#2A3447] pt-4" onSubmit={submit} data-testid="ruling-form">
      <div className="flex flex-wrap gap-2">
        <select className="field w-auto text-xs" value={authority} onChange={(e) => setF({ ...f, authority: e.target.value })} data-testid="ruling-authority-select">
          {options.map((o) => <option key={o} value={o}>{o === "foundry" ? "Propose a Planar Foundry ruling" : LABEL[o]}</option>)}
        </select>
        {authority === "community" && <select className="field w-auto text-xs" value={communityId} onChange={(e) => setF({ ...f, community_id: e.target.value })} data-testid="ruling-community-select">
          {staffComs.map((c) => <option key={c.community_id} value={c.community_id}>{c.name}</option>)}</select>}
      </div>
      <textarea rows={3} className="field" placeholder="Ruling text (at least 10 characters)" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="ruling-text-input" />
      <input className="field" placeholder="Source reference (optional)" value={f.source_reference} onChange={(e) => setF({ ...f, source_reference: e.target.value })} data-testid="ruling-source-input" />
      <button className="btn-gold justify-self-start" disabled={f.text.length < 10} data-testid="ruling-submit-button">Submit ruling</button>
    </form>
  );
}

export function RulingsPanel({ targetType, targetId, ownerId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const key = ["rulings", targetType, targetId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.get("/rulings", { params: { target_type: targetType, target_id: targetId } }).then((r) => r.data) });
  const items = data?.items || [];
  return (
    <section className="panel p-6" data-testid="rulings-panel">
      <p className="eyebrow mb-3 flex items-center gap-2"><Scale className="h-4 w-4" />Rulings</p>
      {data?.custom_text_note && <p className="mb-3 text-xs text-slate-500" data-testid="rulings-custom-note">{data.custom_text_note}</p>}
      <ul className="space-y-3">
        {items.map((r) => (
          <li key={r.ruling_id} className="rounded-md border border-[#2A3447] p-3" data-testid={`ruling-item-${r.ruling_id}`}>
            <div className="flex flex-wrap items-center gap-2"><AuthorityBadge authority={r.authority} />
              {r.community && <span className="font-mono text-[10px] text-sky-300">{r.community.name}</span>}
              {r.status !== "published" && <span className="font-mono text-[10px] uppercase text-copper-300" data-testid="ruling-status">{r.status.replace("_", " ")}</span>}
              <span className="ml-auto font-mono text-[10px] text-slate-500">{(r.published_at || r.created_at || "").slice(0, 10)}</span></div>
            <p className="mt-2 whitespace-pre-line text-sm text-slate-300"><ManaText text={r.text} /></p>
          </li>
        ))}
      </ul>
      {!items.length && <p className="text-sm text-slate-500" data-testid="rulings-empty">No rulings yet.</p>}
      {user && <RulingForm targetType={targetType} targetId={targetId} ownerId={ownerId} onDone={() => qc.invalidateQueries({ queryKey: key })} />}
    </section>
  );
}
